/**
 * Unify Practitioner — extends FHIR R4 Practitioner.
 *
 * Represents providers, supervisors, practice owners/admins, and Unify
 * platform staff. The Practitioner resource stores clinical identity,
 * credentials, licenses, specialties, practice profile, and RBAC metadata.
 *
 * ## Standard FHIR fields used by Unify
 *
 * | Field | Usage |
 * |-------|-------|
 * | `name[0]` (use: `official`) | Legal name (given + family) |
 * | `name[1]` (use: `nickname`) | Preferred / display name (optional) |
 * | `name[2]` (use: `anonymous`) | Alias (0..1, capped to match Salesforce) |
 * | `telecom[]` | Email (`system: 'email'`, `use: 'work'`), phone (`system: 'phone'`, `use: 'work'`) |
 * | `identifier[]` | Email identifier (`unify.health/fhir/identifier/user-email`), NPI (`us-npi`), SSN (`us-ssn`), Salesforce ID (`salesforce-id`), seed tag |
 * | `photo[0]` | Profile photo / headshot (Attachment) |
 * | `qualification[]` | Credentials: licenses, board certifications, DEA, supplemental training |
 * | `active` | Whether the practitioner account is active |
 * | `gender` | Administrative gender |
 * | `birthDate` | Date of birth (collected during onboarding) |
 * | `address[]` | Mailing address |
 * | `communication[]` | Languages spoken (CodeableConcept with `text`) |
 *
 * ## Custom extensions
 *
 * | Extension URL | Value type | Purpose |
 * |---------------|------------|---------|
 * | `EXT_URLS.role` | `valueString` (UnifyRole) | RBAC role slug |
 * | `EXT_URLS.uiPermissions` | `valueString` (JSON) | Resolved UI permission flags |
 * | `EXT_URLS.uiPermissionOverrides` | `valueString` (JSON) | Per-user permission overrides |
 * | `EXT_URLS.supervisingPractitioner` | `valueReference` → Practitioner | Supervisor assignment (canonical) |
 * | `EXT_URLS.superviseePractitioner` | `valueReference` → Practitioner | Reverse supervisee list on supervisor (synced) |
 * | `EXT_URLS.requiresCosignature` | `valueBoolean` | Whether clinical notes require supervisor cosign |
 * | `EXT_URLS.pronouns` | `valueString` | Display pronouns |
 * | `ADMIN_EXT_URLS.organization` | `valueReference` → Organization | Practice membership |
 * | `INTAKE_EXT_URLS.credentials` | `valueString` | Credential abbreviation for intake display |
 * | `INTAKE_EXT_URLS.shortBio` | `valueString` | Short bio for provider directory |
 * | `INTAKE_EXT_URLS.longBio` | `valueString` | Long bio for provider directory |
 * | `INTAKE_EXT_URLS.headshotUrl` | `valueString` | Headshot URL for directory |
 * | `INTAKE_EXT_URLS.sessionFormats` | `valueString` | Comma-separated formats (e.g. "virtual,in-person") |
 * | `INTAKE_EXT_URLS.acceptsInsurance` | `valueBoolean` | Whether provider accepts insurance |
 * | `INTAKE_EXT_URLS.takingNewClients` | `valueBoolean` | Whether provider is accepting new clients |
 * | `INTAKE_EXT_URLS.intakeProvider` | `valueBoolean` | Whether provider is listed in intake directory |
 * | `BIO_EXT` | `valueString` | General bio (Cigna / Salesforce sync) |
 * | `ALIASES_EXT` | `valueString` (JSON) | Legacy alias data (deprecated — now name[2] use: anonymous) |
 * | `CLINICAL_EXT` | `valueString` (JSON) | Clinical profile: { specialties, modalities, ageGroups }; staff Profile also stores DOB/ZIP/demographics — not preferred names |
 * | `PRACTITIONER_DISPLAY_TIMEZONE_EXT` | `valueCode` | IANA zone for UI instant display (not location TZ) |
 * | `ONBOARDING_ATTESTATION_EXT` | `valueString` | Attestation signature captured at onboarding |
 * | `GENDER_IDENTITY_EXT` | `valueString` | Gender identity |
 * | `LGBTQIA_IDENTITIES_EXT` | `valueString` | LGBTQIA+ identity |
 * | `RELIGION_SPIRITUALITY_EXT` | `valueString` | Religion / spirituality |
 * | `RACE_ETHNICITY_EXT` | `valueString` (JSON array) | Race / ethnicity |
 */
import type { HumanName, Practitioner, PractitionerQualification, PractitionerRole, Reference, Extension } from '@medplum/fhirtypes';

import {
  EXT_URLS,
  ADMIN_EXT_URLS,
  INTAKE_EXT_URLS,
  CAQH_EXT_URLS,
  BIO_EXT,
  ALIASES_EXT,
  CLINICAL_EXT,
  CASELOAD_PREFERENCE_EXT,
  PRACTITIONER_DISPLAY_TIMEZONE_EXT,
  ONBOARDING_ATTESTATION_EXT,
  GENDER_IDENTITY_EXT,
  LGBTQIA_IDENTITIES_EXT,
  RELIGION_SPIRITUALITY_EXT,
  RACE_ETHNICITY_EXT,
  NOTIFICATION_PREFERENCES_EXT,
  NUCC_TAXONOMY_SYSTEM,
  PRACTITIONER_ROLE_SYSTEM,
} from '../constants';

// ─── Role values ────────────────────────────────────────────────────

export type UnifyRole =
  | 'unify-admin'
  | 'practice-owner'
  | 'practice-admin'
  | 'supervisor'
  | 'provider';

// ─── Clinical profile JSON shape ────────────────────────────────────

export interface ClinicalProfile {
  specialties?: string[];
  modalities?: string[];
  ageGroups?: string[];
}

export type PractitionerLegalName = { given: string; family: string };

export type PractitionerPreferredNames = { first?: string; last?: string };

function trimNamePart(value: string | undefined): string {
  return (value ?? '').trim();
}

function isLegalNameUse(use: HumanName['use'] | undefined): boolean {
  return use !== 'nickname' && use !== 'anonymous';
}

/** Legal given/family: official HumanName, else name[0] when it is not nickname/anonymous. */
export function resolvePractitionerLegalName(resource: Practitioner): PractitionerLegalName {
  const names = resource.name ?? [];
  const official = names.find((n) => n.use === 'official');
  const fallback = names[0];
  const src =
    official ??
    (fallback && isLegalNameUse(fallback.use) ? fallback : undefined);
  return {
    given: trimNamePart(src?.given?.[0]),
    family: trimNamePart(src?.family),
  };
}

/**
 * Build nickname HumanName from preferred vs legal slots.
 * Preferred equal to legal (trim, case-sensitive) is treated as absent.
 */
export function buildPractitionerNicknameName(
  legal: PractitionerLegalName,
  preferred: PractitionerPreferredNames,
): HumanName | undefined {
  const legalGiven = trimNamePart(legal.given);
  const legalFamily = trimNamePart(legal.family);
  let first = trimNamePart(preferred.first);
  let last = trimNamePart(preferred.last);
  if (first === legalGiven) first = '';
  if (last === legalFamily) last = '';
  if (!first && !last) return undefined;
  const given = first || legalGiven;
  const family = last || legalFamily;
  return {
    use: 'nickname',
    ...(given ? { given: [given] } : {}),
    ...(family ? { family } : {}),
  };
}

export function parsePractitionerPreferredNames(
  legal: PractitionerLegalName,
  names: HumanName[] | undefined,
): { preferredFirstName: string; preferredLastName: string } {
  const nick = names?.find((n) => n.use === 'nickname');
  if (!nick) {
    return { preferredFirstName: '', preferredLastName: '' };
  }
  const legalGiven = trimNamePart(legal.given);
  const legalFamily = trimNamePart(legal.family);
  const nickGiven = trimNamePart(nick.given?.[0]);
  const nickFamily = trimNamePart(nick.family);
  return {
    preferredFirstName: nickGiven && nickGiven !== legalGiven ? nickGiven : '',
    preferredLastName: nickFamily && nickFamily !== legalFamily ? nickFamily : '',
  };
}

/** Nickname parse first; leftover CLINICAL_EXT preferred keys only if both slots empty. */
export function resolvePractitionerPreferredNamesForForm(
  resource: Practitioner,
): { preferredFirstName: string; preferredLastName: string } {
  const parsed = parsePractitionerPreferredNames(
    resolvePractitionerLegalName(resource),
    resource.name,
  );
  if (parsed.preferredFirstName || parsed.preferredLastName) {
    return parsed;
  }
  const raw = getExt(resource, CLINICAL_EXT)?.valueString;
  if (!raw) {
    return parsed;
  }
  try {
    const clinical = JSON.parse(raw) as { preferredFirstName?: unknown; preferredLastName?: unknown };
    const legal = resolvePractitionerLegalName(resource);
    const first = typeof clinical.preferredFirstName === 'string'
      ? trimNamePart(clinical.preferredFirstName)
      : '';
    const last = typeof clinical.preferredLastName === 'string'
      ? trimNamePart(clinical.preferredLastName)
      : '';
    return {
      preferredFirstName: first && first !== legal.given ? first : '',
      preferredLastName: last && last !== legal.family ? last : '',
    };
  } catch {
    return parsed;
  }
}

/**
 * Header display strings: nickname text as `preferred`, resolved legal name as `legal`.
 * `preferred` is empty when there is no nickname or it matches the legal name.
 */
export function resolvePractitionerNameDisplay(
  resource: Practitioner,
): { preferred: string; legal: string } {
  const names = resource.name ?? [];
  const legalParts = resolvePractitionerLegalName(resource);
  const legalSource = names.find((n) => n.use === 'official') ?? (
    names[0] && isLegalNameUse(names[0].use) ? names[0] : undefined
  );
  const legal =
    [legalParts.given, legalParts.family].filter(Boolean).join(' ') || trimNamePart(legalSource?.text);
  const nick = names.find((n) => n.use === 'nickname');
  const preferred = nick
    ? [trimNamePart(nick.given?.[0]), trimNamePart(nick.family)].filter(Boolean).join(' ')
      || trimNamePart(nick.text)
    : '';
  return { preferred: preferred && preferred !== legal ? preferred : '', legal };
}

function legalHumanName(resource: Practitioner): HumanName | undefined {
  const names = resource.name ?? [];
  return names.find((n) => n.use === 'official')
    ?? (names[0] && isLegalNameUse(names[0].use) ? names[0] : undefined);
}

function withLegalAffixes(core: string, resource: Practitioner): string {
  if (!core) return '';
  const src = legalHumanName(resource);
  const prefix = (src?.prefix ?? []).map(trimNamePart).filter(Boolean).join(' ');
  const suffix = (src?.suffix ?? []).map(trimNamePart).filter(Boolean).join(' ');
  return [prefix, core, suffix].filter(Boolean).join(' ');
}

/** Client-facing / sort key: preferred core with legal prefix/suffix, else legal. */
export function formatPractitionerPreferredLabel(resource: Practitioner): string {
  return formatPractitionerNameParts(resource).primary;
}

/** Staff selects/titles: preferred (legal) when they differ; prefix/suffix from legal name. */
export function formatPractitionerStaffLabel(resource: Practitioner): string {
  const { primary, legal } = formatPractitionerNameParts(resource);
  if (!legal || primary.endsWith(` (${legal})`)) return primary;
  return `${primary} (${legal})`;
}

/** Two-tone UI: `primary` is preferred (or legal if none); `legal` only when it differs. */
export function formatPractitionerNameParts(
  resource: Practitioner,
): { primary: string; legal?: string } {
  const { preferred, legal } = resolvePractitionerNameDisplay(resource);
  const primary = withLegalAffixes(preferred || legal, resource);
  if (preferred && legal && preferred !== legal) {
    return { primary, legal: withLegalAffixes(legal, resource) };
  }
  return { primary };
}

/**
 * Drop legacy `CLINICAL_EXT` preferred-name keys so a cleared nickname cannot be
 * resurrected by the form hydrate fallback. Other clinical JSON is preserved.
 */
export function stripPractitionerLegacyPreferredNames<T extends Practitioner>(resource: T): T {
  const raw = getExt(resource, CLINICAL_EXT)?.valueString;
  if (!raw) {
    return resource;
  }
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return resource;
  }
  if (!('preferredFirstName' in parsed) && !('preferredLastName' in parsed)) {
    return resource;
  }
  const { preferredFirstName: _first, preferredLastName: _last, ...rest } = parsed;
  const extensions = (resource.extension ?? []).filter((e) => e.url !== CLINICAL_EXT);
  if (Object.keys(rest).length > 0) {
    extensions.push({ url: CLINICAL_EXT, valueString: JSON.stringify(rest) });
  }
  return { ...resource, extension: extensions };
}

function insertNickname(names: HumanName[], built: HumanName): HumanName[] {
  const officialIdx = names.findIndex((n) => n.use === 'official');
  const legalIdx =
    officialIdx >= 0
      ? officialIdx
      : names.findIndex((n) => isLegalNameUse(n.use));
  const anonIdx = names.findIndex((n) => n.use === 'anonymous');
  let insertAt: number;
  if (anonIdx >= 0) {
    insertAt = anonIdx;
  } else if (legalIdx >= 0) {
    insertAt = legalIdx + 1;
  } else {
    insertAt = names.length;
  }
  const next = [...names];
  next.splice(insertAt, 0, built);
  return next;
}

function moveOfficialToFront(names: HumanName[]): HumanName[] {
  const officialIdx = names.findIndex((n) => n.use === 'official');
  if (officialIdx > 0) {
    const next = [...names];
    const [off] = next.splice(officialIdx, 1);
    next.unshift(off);
    return next;
  }
  return names;
}

/**
 * Upsert the nickname entry, collapsing duplicates and restoring the documented
 * order: official/legal first, then nickname, then anonymous.
 */
export function upsertPractitionerNickname<T extends Practitioner>(
  resource: T,
  preferred: PractitionerPreferredNames,
): T {
  const legal = resolvePractitionerLegalName(resource);
  const built = buildPractitionerNicknameName(legal, preferred);
  const withoutNicknames = moveOfficialToFront(resource.name ?? []).filter((n) => n.use !== 'nickname');
  return {
    ...resource,
    name: built ? insertNickname(withoutNicknames, built) : withoutNicknames,
  };
}

/** Upsert legal name by use official — never mutate name[0] by index. */
export function upsertPractitionerOfficialName<T extends Practitioner>(
  resource: T,
  official: Pick<HumanName, 'given' | 'family' | 'text'>,
): T {
  const nextOfficial: HumanName = {
    use: 'official',
    ...(official.given ? { given: official.given } : {}),
    ...(official.family ? { family: official.family } : {}),
    ...(official.text ? { text: official.text } : {}),
  };
  const names = [...(resource.name ?? [])];
  const idx = names.findIndex((n) => n.use === 'official');
  if (idx >= 0) {
    names[idx] = { ...names[idx], ...nextOfficial, use: 'official' };
  } else {
    names.unshift(nextOfficial);
  }
  const officialIdx = names.findIndex((n) => n.use === 'official');
  if (officialIdx > 0) {
    const [off] = names.splice(officialIdx, 1);
    names.unshift(off);
  }
  return { ...resource, name: names };
}

export function splitDisplayNameToOfficialParts(fullName: string): Pick<HumanName, 'given' | 'family'> {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const family = parts.length > 1 ? parts.pop() ?? '' : '';
  const given = parts.length ? parts : (fullName.trim() ? [fullName.trim()] : undefined);
  return { given, family: family || undefined };
}

// ─── Private helpers ────────────────────────────────────────────────

function getExt(resource: Practitioner, url: string): Extension | undefined {
  return resource.extension?.find((e) => e.url === url);
}

function setExt(resource: Practitioner, url: string, ext: Partial<Extension>): Practitioner {
  const extensions = (resource.extension ?? []).filter((e) => e.url !== url);
  extensions.push({ url, ...ext } as Extension);
  return { ...resource, extension: extensions };
}

// ─── Role ───────────────────────────────────────────────────────────

export function getRole(resource: Practitioner): UnifyRole | undefined {
  // Primary/default role is the first role extension. Legacy writers stored
  // valueCode; fall back for old resources.
  const roles = getRoles(resource);
  return roles[0];
}

/**
 * All functional/default role slugs on the profile (repeatable `role` extension).
 * First entry is the primary/default UI role.
 */
export function getRoles(resource: Practitioner): UnifyRole[] {
  const roles: UnifyRole[] = [];
  for (const ext of resource.extension ?? []) {
    if (ext.url !== EXT_URLS.role) continue;
    const code = (ext.valueString ?? ext.valueCode) as UnifyRole | undefined;
    if (code && !roles.includes(code)) roles.push(code);
  }
  return roles;
}

function practitionerRoleCodes(practitionerRole: PractitionerRole | undefined): UnifyRole[] {
  if (!practitionerRole) return [];
  const codes: UnifyRole[] = [];
  for (const concept of practitionerRole.code ?? []) {
    for (const coding of concept.coding ?? []) {
      if (coding.system !== PRACTITIONER_ROLE_SYSTEM || !coding.code || coding.code === 'client') continue;
      const code = coding.code as UnifyRole;
      if (codes.includes(code)) continue;
      codes.push(code);
    }
  }
  return codes;
}

/**
 * Assigned functional role slugs: profile `role` extensions ∪ PractitionerRole.code[].
 * Primary (first profile extension) is first; remaining codes follow alphabetically.
 */
export function getAssignedRoleSlugs(
  practitioner: Practitioner,
  practitionerRole?: PractitionerRole,
): UnifyRole[] {
  const fromProfile = getRoles(practitioner);
  const fromCode = practitionerRoleCodes(practitionerRole);
  if (fromProfile.length === 0) return fromCode;
  const seen = new Set(fromProfile);
  const rest = fromCode.filter((role) => !seen.has(role)).sort();
  return [...fromProfile, ...rest];
}

export function setRole(resource: Practitioner, role: UnifyRole): Practitioner {
  return setRoles(resource, role, []);
}

/**
 * Persist primary/default role plus additional functional roles as repeatable
 * `role` extensions. Primary is always written first so `getRole` stays stable.
 */
/** Remove all `role` extensions (offboard / account-only reactivate). */
export function clearRoles(resource: Practitioner): Practitioner {
  return {
    ...resource,
    extension: (resource.extension ?? []).filter((e) => e.url !== EXT_URLS.role),
  };
}

export function setRoles(
  resource: Practitioner,
  primary: UnifyRole,
  additional: UnifyRole[] = [],
): Practitioner {
  const kept = (resource.extension ?? []).filter((e) => e.url !== EXT_URLS.role);
  const ordered = [primary, ...additional.filter((r) => r !== primary)];
  const unique: UnifyRole[] = [];
  for (const role of ordered) {
    if (!unique.includes(role)) unique.push(role);
  }
  return {
    ...resource,
    extension: [
      ...kept,
      ...unique.map((role) => ({ url: EXT_URLS.role, valueString: role })),
    ],
  };
}

// ─── UI permissions ─────────────────────────────────────────────────

export function getUiPermissions(resource: Practitioner): Record<string, boolean> | undefined {
  const raw = getExt(resource, EXT_URLS.uiPermissions)?.valueString;
  return raw ? (JSON.parse(raw) as Record<string, boolean>) : undefined;
}

export function setUiPermissions(resource: Practitioner, perms: Record<string, boolean>): Practitioner {
  return setExt(resource, EXT_URLS.uiPermissions, { valueString: JSON.stringify(perms) });
}

// ─── UI permission overrides ────────────────────────────────────────

export function getUiPermissionOverrides(resource: Practitioner): Record<string, boolean> | undefined {
  const raw = getExt(resource, EXT_URLS.uiPermissionOverrides)?.valueString;
  return raw ? (JSON.parse(raw) as Record<string, boolean>) : undefined;
}

export function setUiPermissionOverrides(resource: Practitioner, overrides: Record<string, boolean>): Practitioner {
  return setExt(resource, EXT_URLS.uiPermissionOverrides, { valueString: JSON.stringify(overrides) });
}

// ─── Supervising practitioner ───────────────────────────────────────

/** All supervisor references on this practitioner (canonical edges; usually 0..1). */
export function getSupervisorRefs(resource: Practitioner): Reference[] {
  const refs: Reference[] = [];
  const seen = new Set<string>();
  for (const ext of resource.extension ?? []) {
    if (ext.url !== EXT_URLS.supervisingPractitioner) continue;
    const ref = ext.valueReference;
    if (!ref?.reference || seen.has(ref.reference)) continue;
    seen.add(ref.reference);
    refs.push(ref);
  }
  return refs;
}

/** Replace all supervisor edges. Empty array clears. Prefer a single entry (product expectation). */
export function setSupervisorRefs(resource: Practitioner, refs: Reference[]): Practitioner {
  const without = (resource.extension ?? []).filter(
    (e) => e.url !== EXT_URLS.supervisingPractitioner,
  );
  const seen = new Set<string>();
  const next: Extension[] = [...without];
  for (const ref of refs) {
    if (!ref.reference || seen.has(ref.reference)) continue;
    seen.add(ref.reference);
    next.push({ url: EXT_URLS.supervisingPractitioner, valueReference: ref });
  }
  return { ...resource, extension: next.length > 0 ? next : undefined };
}

/** First supervisor reference (handoff / SPRF / default prefill). */
export function getSupervisingPractitionerRef(resource: Practitioner): Reference | undefined {
  return getSupervisorRefs(resource)[0];
}

export function setSupervisingPractitionerRef(resource: Practitioner, ref: Reference): Practitioner {
  return setSupervisorRefs(resource, [ref]);
}

export function removeSupervisingPractitioner(resource: Practitioner): Practitioner {
  return setSupervisorRefs(resource, []);
}

// ─── Requires cosignature ───────────────────────────────────────────

/** True when clinical documentation by this practitioner must be cosigned. Defaults false if unset. */
export function getRequiresCosignature(resource: Practitioner | undefined): boolean {
  if (!resource) return false;
  return getExt(resource, EXT_URLS.requiresCosignature)?.valueBoolean === true;
}

export function setRequiresCosignature(resource: Practitioner, requires: boolean): Practitioner {
  return setExt(resource, EXT_URLS.requiresCosignature, { valueBoolean: requires });
}

/**
 * Canonical cosign gate — reads `requires-cosignature` only.
 * Do not infer from supervising-practitioner presence.
 */
export function practitionerRequiresCosignature(
  practitioner: Practitioner | { extension?: Extension[] } | undefined,
): boolean {
  if (!practitioner?.extension?.length) return false;
  return (
    practitioner.extension.find((e) => e.url === EXT_URLS.requiresCosignature)?.valueBoolean === true
  );
}

// ─── Pronouns ───────────────────────────────────────────────────────

export function getPractitionerPronouns(resource: Practitioner): string | undefined {
  return getExt(resource, EXT_URLS.pronouns)?.valueString;
}

export function setPractitionerPronouns(resource: Practitioner, pronouns: string): Practitioner {
  return setExt(resource, EXT_URLS.pronouns, { valueString: pronouns });
}

// ─── Organization (practice membership) ─────────────────────────────

export function getPractitionerOrganizationRef(resource: Practitioner): Reference | undefined {
  return getExt(resource, ADMIN_EXT_URLS.organization)?.valueReference;
}

export function setPractitionerOrganizationRef(resource: Practitioner, ref: Reference): Practitioner {
  return setExt(resource, ADMIN_EXT_URLS.organization, { valueReference: ref });
}

// ─── Intake directory: credentials ──────────────────────────────────

export function getCredentials(resource: Practitioner): string | undefined {
  return getExt(resource, INTAKE_EXT_URLS.credentials)?.valueString;
}

export function setCredentials(resource: Practitioner, credentials: string): Practitioner {
  return setExt(resource, INTAKE_EXT_URLS.credentials, { valueString: credentials });
}

// ─── Intake directory: short bio ────────────────────────────────────

export function getShortBio(resource: Practitioner): string | undefined {
  return getExt(resource, INTAKE_EXT_URLS.shortBio)?.valueString;
}

export function setShortBio(resource: Practitioner, bio: string): Practitioner {
  return setExt(resource, INTAKE_EXT_URLS.shortBio, { valueString: bio });
}

// ─── Intake directory: long bio ─────────────────────────────────────

export function getLongBio(resource: Practitioner): string | undefined {
  return getExt(resource, INTAKE_EXT_URLS.longBio)?.valueString;
}

export function setLongBio(resource: Practitioner, bio: string): Practitioner {
  return setExt(resource, INTAKE_EXT_URLS.longBio, { valueString: bio });
}

// ─── Intake directory: headshot URL ─────────────────────────────────

export function getHeadshotUrl(resource: Practitioner): string | undefined {
  return getExt(resource, INTAKE_EXT_URLS.headshotUrl)?.valueString;
}

export function setHeadshotUrl(resource: Practitioner, url: string): Practitioner {
  return setExt(resource, INTAKE_EXT_URLS.headshotUrl, { valueString: url });
}

// ─── Intake directory: session formats ──────────────────────────────

export function getSessionFormats(resource: Practitioner): string | undefined {
  return getExt(resource, INTAKE_EXT_URLS.sessionFormats)?.valueString;
}

export function setSessionFormats(resource: Practitioner, formats: string): Practitioner {
  return setExt(resource, INTAKE_EXT_URLS.sessionFormats, { valueString: formats });
}

// ─── Intake directory: accepts insurance ────────────────────────────

export function getAcceptsInsurance(resource: Practitioner): boolean | undefined {
  return getExt(resource, INTAKE_EXT_URLS.acceptsInsurance)?.valueBoolean;
}

export function setAcceptsInsurance(resource: Practitioner, value: boolean): Practitioner {
  return setExt(resource, INTAKE_EXT_URLS.acceptsInsurance, { valueBoolean: value });
}

// ─── Intake directory: taking new clients ───────────────────────────

export function isTakingNewClients(resource: Practitioner): boolean {
  return getExt(resource, INTAKE_EXT_URLS.takingNewClients)?.valueBoolean ?? false;
}

export function setTakingNewClients(resource: Practitioner, value: boolean): Practitioner {
  return setExt(resource, INTAKE_EXT_URLS.takingNewClients, { valueBoolean: value });
}

// ─── Intake directory: intake provider flag ─────────────────────────

export function isIntakeProvider(resource: Practitioner): boolean {
  return getExt(resource, INTAKE_EXT_URLS.intakeProvider)?.valueBoolean ?? false;
}

export function setIntakeProvider(resource: Practitioner, value: boolean): Practitioner {
  return setExt(resource, INTAKE_EXT_URLS.intakeProvider, { valueBoolean: value });
}

// ─── Bio (Cigna / Salesforce sync) ──────────────────────────────────

export function getBio(resource: Practitioner): string | undefined {
  return getExt(resource, BIO_EXT)?.valueString;
}

export function setBio(resource: Practitioner, bio: string): Practitioner {
  return setExt(resource, BIO_EXT, { valueString: bio });
}

// ─── Aliases (legacy JSON extension) ────────────────────────────────

export function getAliases(resource: Practitioner): unknown | undefined {
  const raw = getExt(resource, ALIASES_EXT)?.valueString;
  return raw ? JSON.parse(raw) : undefined;
}

export function setAliases(resource: Practitioner, aliases: unknown): Practitioner {
  return setExt(resource, ALIASES_EXT, { valueString: JSON.stringify(aliases) });
}

// ─── Clinical profile (specialties, modalities, age groups) ─────────

export function getClinical(resource: Practitioner): ClinicalProfile | undefined {
  const raw = getExt(resource, CLINICAL_EXT)?.valueString;
  return raw ? (JSON.parse(raw) as ClinicalProfile) : undefined;
}

export function setClinical(resource: Practitioner, profile: ClinicalProfile): Practitioner {
  return setExt(resource, CLINICAL_EXT, { valueString: JSON.stringify(profile) });
}

// ─── Caseload preference ──────────────────────────────────────────────

export function getCaseloadPreference(resource: Practitioner): string | undefined {
  return getExt(resource, CASELOAD_PREFERENCE_EXT)?.valueString;
}

export function setCaseloadPreference(resource: Practitioner, value: string): Practitioner {
  return setExt(resource, CASELOAD_PREFERENCE_EXT, { valueString: value });
}

// ─── Display timezone (UI instants only) ────────────────────────────

export function getPractitionerDisplayTimeZone(resource: Practitioner): string | undefined {
  const ext = getExt(resource, PRACTITIONER_DISPLAY_TIMEZONE_EXT);
  const raw = ext?.valueCode ?? ext?.valueString;
  const tz = raw?.trim();
  return tz || undefined;
}

export function setPractitionerDisplayTimeZone(
  resource: Practitioner,
  value: string | undefined,
): Practitioner {
  const tz = value?.trim();
  if (!tz) {
    const extension = (resource.extension ?? []).filter((e) => e.url !== PRACTITIONER_DISPLAY_TIMEZONE_EXT);
    return { ...resource, extension: extension.length ? extension : undefined };
  }
  return setExt(resource, PRACTITIONER_DISPLAY_TIMEZONE_EXT, { valueCode: tz });
}

// ─── Onboarding attestation ─────────────────────────────────────────

export function hasOnboardingAttestation(resource: Practitioner): boolean {
  return resource.extension?.some((e) => e.url === ONBOARDING_ATTESTATION_EXT) ?? false;
}

export function getOnboardingAttestation(resource: Practitioner): string | undefined {
  return getExt(resource, ONBOARDING_ATTESTATION_EXT)?.valueString;
}

export function setOnboardingAttestation(resource: Practitioner, signature: string): Practitioner {
  return setExt(resource, ONBOARDING_ATTESTATION_EXT, { valueString: signature });
}

// ─── Demographics: gender identity ──────────────────────────────────

export function getPractitionerGenderIdentity(resource: Practitioner): string | undefined {
  return getExt(resource, GENDER_IDENTITY_EXT)?.valueString;
}

export function setPractitionerGenderIdentity(resource: Practitioner, value: string): Practitioner {
  return setExt(resource, GENDER_IDENTITY_EXT, { valueString: value });
}

// ─── Demographics: LGBTQIA+ identity ────────────────────────────────

export function getPractitionerLgbtqiaIdentities(resource: Practitioner): string | undefined {
  return getExt(resource, LGBTQIA_IDENTITIES_EXT)?.valueString;
}

export function setPractitionerLgbtqiaIdentities(resource: Practitioner, value: string): Practitioner {
  return setExt(resource, LGBTQIA_IDENTITIES_EXT, { valueString: value });
}

// ─── Demographics: religion / spirituality ──────────────────────────

export function getPractitionerReligionSpirituality(resource: Practitioner): string | undefined {
  return getExt(resource, RELIGION_SPIRITUALITY_EXT)?.valueString;
}

export function setPractitionerReligionSpirituality(resource: Practitioner, value: string): Practitioner {
  return setExt(resource, RELIGION_SPIRITUALITY_EXT, { valueString: value });
}

// ─── Demographics: race / ethnicity ─────────────────────────────────

export function getPractitionerRaceEthnicity(resource: Practitioner): string | undefined {
  return getExt(resource, RACE_ETHNICITY_EXT)?.valueString;
}

export function setPractitionerRaceEthnicity(resource: Practitioner, value: string): Practitioner {
  return setExt(resource, RACE_ETHNICITY_EXT, { valueString: value });
}

// ─── Notification preferences ───────────────────────────────────────

export function getNotificationPreferences<T>(resource: Practitioner): T | undefined {
  const raw = getExt(resource, NOTIFICATION_PREFERENCES_EXT)?.valueString;
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

export function setNotificationPreferences<T>(resource: Practitioner, prefs: T): Practitioner {
  return setExt(resource, NOTIFICATION_PREFERENCES_EXT, { valueString: JSON.stringify(prefs) });
}

// ─── CAQH ─────────────────────────────────────────────────────────────

export function getCaqhId(resource: Practitioner): string | undefined {
  return getExt(resource, CAQH_EXT_URLS.id)?.valueString;
}

export function setCaqhId(resource: Practitioner, value: string): Practitioner {
  return setExt(resource, CAQH_EXT_URLS.id, { valueString: value });
}

export function getCaqhUsername(resource: Practitioner): string | undefined {
  return getExt(resource, CAQH_EXT_URLS.username)?.valueString;
}

export function setCaqhUsername(resource: Practitioner, value: string): Practitioner {
  return setExt(resource, CAQH_EXT_URLS.username, { valueString: value });
}

export function getCaqhPassword(resource: Practitioner): string | undefined {
  return getExt(resource, CAQH_EXT_URLS.password)?.valueString;
}

export function setCaqhPassword(resource: Practitioner, value: string): Practitioner {
  return setExt(resource, CAQH_EXT_URLS.password, { valueString: value });
}

export function getCaqhAttestationDate(resource: Practitioner): string | undefined {
  return getExt(resource, CAQH_EXT_URLS.attestationDate)?.valueDate;
}

export function setCaqhAttestationDate(resource: Practitioner, value: string): Practitioner {
  return setExt(resource, CAQH_EXT_URLS.attestationDate, { valueDate: value });
}

// ─── NUCC taxonomy (billing rendering provider) ─────────────────────

type Qualification = NonNullable<Practitioner['qualification']>[number];

export function isNuccTaxonomyQualification(qualification: Qualification): boolean {
  return qualification.code?.coding?.some((coding) => coding.system === NUCC_TAXONOMY_SYSTEM) ?? false;
}

export function buildNuccTaxonomyQualification(code: string, display?: string): PractitionerQualification {
  const trimmedCode = code.trim();
  return {
    code: {
      coding: [{ system: NUCC_TAXONOMY_SYSTEM, code: trimmedCode, ...(display ? { display } : {}) }],
      text: display ?? trimmedCode,
    },
  };
}

export function getNuccTaxonomyCode(resource: Practitioner): string | undefined {
  const qualification = resource.qualification?.find(isNuccTaxonomyQualification);
  return qualification?.code?.coding?.find((coding) => coding.system === NUCC_TAXONOMY_SYSTEM)?.code?.trim();
}

export function upsertNuccTaxonomyQualification(
  resource: Practitioner,
  code: string,
  display?: string,
): Practitioner {
  const trimmedCode = code.trim();
  const otherQualifications = (resource.qualification ?? []).filter((qualification) => !isNuccTaxonomyQualification(qualification));

  if (!trimmedCode) {
    return {
      ...resource,
      qualification: otherQualifications.length > 0 ? otherQualifications : undefined,
    };
  }

  return {
    ...resource,
    qualification: [buildNuccTaxonomyQualification(trimmedCode, display), ...otherQualifications],
  };
}
